import { vesselNoticeHeading, type EmployeeNotification } from '../api/employeeNotificationsApi';

/**
 * Desktop pop-ups for vessel notices (browser Notification API): they show over other windows while
 * a Maleva tab is open, also in the background or minimised. With no Maleva tab open nothing shows
 * (that would need service-worker Web Push and a backend change).
 */
export type DesktopAlertState = NotificationPermission | 'unsupported';

const ICON = '/appLogo.svg';

export const desktopAlertState = (): DesktopAlertState =>
  typeof window !== 'undefined' && 'Notification' in window ? window.Notification.permission : 'unsupported';

/** Asks the browser once; returns what the user chose. */
export async function askForDesktopAlerts(): Promise<DesktopAlertState> {
  if (desktopAlertState() === 'unsupported') return 'unsupported';
  try {
    // older Safari answers through a callback and returns nothing
    const answer = await window.Notification.requestPermission();
    return answer ?? desktopAlertState();
  } catch {
    return desktopAlertState();
  }
}

/**
 * Shows the notice as a silent pop-up that stays until the user acts (owner, 2026-10-13: no sound in
 * Maleva on the web, so the system's own notification sound is turned off too). A click brings the
 * tab to the front, runs `onOpen` and closes the pop-up. Returns null when not allowed or the browser refuses.
 */
export function showDesktopAlert(notice: EmployeeNotification, onOpen: () => void): Notification | null {
  if (desktopAlertState() !== 'granted') return null;
  try {
    const popUp = new window.Notification(notice.title, {
      body: notice.body || vesselNoticeHeading(notice.kind),
      tag: `vessel-${notice.id}`,
      requireInteraction: true,
      silent: true,
      icon: ICON,
    });
    popUp.onclick = () => {
      window.focus();
      onOpen();
      popUp.close();
    };
    return popUp;
  } catch {
    // e.g. Chrome on Android allows pop-ups only from a service worker
    return null;
  }
}
